import React, { useRef, useState, type JSX } from 'react';
import { makeStyles } from 'tss-react/mui';
// eslint-disable-next-line aveny/mui-import-rule
import { InputAdornment, Theme } from '@mui/material';
import { ISortInfo } from '../../../../../BaseComponents/Table/Components/SortableTableUtils';
import { ICollectionDataType } from '../BusinessCollectionComponents/Queries/BusinessCollectionQuery';
import { EDCard } from '../../../../../BaseComponents/Card/EDCard';
import { TVisningstyp } from '../BusinessCollectionComponents/BusinessDataSet/BusinessDataSet';
import { useIsSmallScreen } from '../../../../../Hooks/useFullscreenView';
import EDTypography from '../../../../../BaseComponents/Typography/EDTypography';
import debounce from 'debounce';
import { IFilterAttribute } from '../../Const/Delsystem';
import EDIcon from '../../../../../BaseComponents/Icon/EDIcon';
import EDBox from '../../../../../BaseComponents/EDBox/EDBox';
import { EDTextEdit } from '../../../../../BaseComponents/TextEdit/EDTextEdit';

interface IProps {
  onSearchTextChanged: (text: string) => void;
  searchText: string;
  sortedData: ISortInfo<ICollectionDataType>;
  filterAttributes: IFilterAttribute[] | undefined;
  filterAttributesPlacholder?: string;
  listlength: number;
  sortedLength: number;
  visningsTyp: TVisningstyp;
  selectionNameComp?: JSX.Element | null;
  width?: string | number;
}

const useStyles = makeStyles()((theme: Theme) => ({
  input: {
    backgroundColor: theme.palette.common.white,
  },
  cardSubContainer: {
    paddingTop: theme.spacing(0),
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingBottom: theme.spacing(1),
  },
  adornment: {
    marginRight: theme.spacing(1),
  },
  adornmentMobile: {
    marginRight: theme.spacing(0.5),
  },
}));

export const ListFilteringComponent: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const { classes } = useStyles();
  const isSmallScreen = useIsSmallScreen();
  const [searchText, setSearchText] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const debouncedOnSearchTextChanged = useRef(
    debounce((newText: string, callBack: (...args: any[]) => void) => {
      callBack(newText);
    }, 500)
  ).current;
  const filterIcon = (
    <InputAdornment position="start">
      <EDBox
        sx={[
          (isFocused || (searchText != null && searchText !== '')) && {
            display: 'none',
          },
        ]}
      >
        <EDIcon iconType="filter_alt" color="disabled" />
      </EDBox>
    </InputAdornment>
  );
  const endAdornment = (
    <EDTypography
      color="textSecondary"
      className={isSmallScreen ? classes.adornmentMobile : classes.adornment}
      variant={'caption'}
    >
      {props.searchText ? props.sortedLength : props.listlength}/
      {props.listlength}
    </EDTypography>
  );
  const onFocus = () => {
    setIsFocused(true);
  };

  const onBlur = () => {
    setIsFocused(false);
  };
  const onChange = (newText: string) => {
    setSearchText(newText);
    debouncedOnSearchTextChanged(newText, props.onSearchTextChanged);
  };

  return (
    <EDBox
      sx={
        !isSmallScreen && props.visningsTyp === 'kort'
          ? {
              width: '100%',
              paddingTop: theme => theme.spacing(1),
              paddingBottom: theme => theme.spacing(1),
              paddingRight: theme => theme.spacing(0.5),
              paddingLeft: theme => theme.spacing(0.5),
              boxSizing: 'border-box',
            }
          : {
              width: '100%',
              paddingTop: theme => theme.spacing(1),
              paddingBottom: theme => theme.spacing(1),
              boxSizing: 'border-box',
            }
      }
    >
      <EDBox
        sx={
          isSmallScreen && props.visningsTyp === 'kort'
            ? {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                marginRight: theme => theme.spacing(0.5),
                marginLeft: theme => theme.spacing(0.5),
              }
            : {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                padding: theme =>
                  props.visningsTyp === 'kort' ? 0 : theme.spacing(0, 2),
              }
        }
      >
        {props.visningsTyp === 'kort' ? (
          <EDCard width="100%">
            <div className={classes.cardSubContainer}>
              <EDTextEdit
                label={
                  props.filterAttributes
                    ? props.filterAttributesPlacholder ?? ''
                    : ''
                }
                onChange={onChange}
                onFocus={onFocus}
                onBlur={onBlur}
                value={searchText}
                endAdornment={endAdornment}
                startAdornment={filterIcon}
                testId="filterTextEdit"
                largeStartAdornmentPadding
              />
              {props.selectionNameComp}
            </div>
          </EDCard>
        ) : (
          <EDBox
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-around',
            }}
          >
            <EDTextEdit
              label={
                props.filterAttributes
                  ? props.filterAttributesPlacholder ?? ''
                  : ''
              }
              onChange={onChange}
              onFocus={onFocus}
              onBlur={onBlur}
              value={searchText}
              endAdornment={endAdornment}
              startAdornment={filterIcon}
              width={props.width}
              size="large"
              allMargins="none"
              largeStartAdornmentPadding
              testId="filterTextEdit"
            />
            {props.selectionNameComp}
          </EDBox>
        )}
      </EDBox>
    </EDBox>
  );
};
export default ListFilteringComponent;
